import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import './Resume.css';

const RESUME_SOURCE_URL = '/assets/files/resume.json';

const monthMap = {
    january: 0,
    february: 1,
    march: 2,
    april: 3,
    may: 4,
    june: 5,
    july: 6,
    august: 7,
    september: 8,
    october: 9,
    november: 10,
    december: 11
};

const parseResumeDate = (value) => {
    if (!value || value === 'Present') {
        return new Date();
    }

    const normalized = value.toLowerCase().replace(/,/g, '').replace(/\b(\d+)(st|nd|rd|th)\b/g, '$1');
    const [monthName, maybeDayOrYear, maybeYear] = normalized.split(/\s+/);
    const month = monthMap[monthName];
    const year = Number(maybeYear || maybeDayOrYear);

    if (month === undefined || Number.isNaN(year)) {
        return null;
    }

    return new Date(year, month, 1);
};

const list = (value) => Array.isArray(value) ? value : [];

const InlineText = ({ children }) => {
    const text = String(children || '');
    const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);

    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={index}>{part.slice(2, -2)}</strong>;
        }

        return <React.Fragment key={index}>{part}</React.Fragment>;
    });
};

const calculateTimeDifference = (date1, date2) => {
    const startDate = parseResumeDate(date1);
    const endDate = parseResumeDate(date2);

    if (!startDate || !endDate || endDate < startDate) {
        return null;
    }

    const monthsDifference = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
    const years = Math.floor(monthsDifference / 12);
    const months = monthsDifference % 12;

    return { years, months };
};

const formatDuration = (duration) => {
    if (!duration) {
        return '';
    }

    const parts = [];

    if (duration.years > 0) {
        parts.push(`${duration.years} yr`);
    }

    if (duration.months > 0) {
        parts.push(`${duration.months} mos`);
    }

    return parts.length > 0 ? ` (${parts.join(' ')})` : '';
};

const formatSkill = (skill) => {
    if (typeof skill === 'string') {
        return <InlineText>{skill}</InlineText>;
    }

    const keywords = list(skill.keywords).join(', ');

    return (
        <>
            {skill.name && <strong>{skill.name}: </strong>}
            <InlineText>{keywords}</InlineText>
        </>
    );
};

function Resume() {
    const [resumeData, setResumeData] = useState(null);
    const [resumeError, setResumeError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        fetch(RESUME_SOURCE_URL)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Unable to load resume data (${response.status})`);
                }

                return response.json();
            })
            .then((data) => {
                if (isMounted) {
                    setResumeData(data);
                }
            })
            .catch((error) => {
                if (isMounted) {
                    setResumeError(error);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const work = list(resumeData?.work);
    const skills = list(resumeData?.skills);
    const education = list(resumeData?.education);
    const awards = list(resumeData?.awards);
    const projects = list(resumeData?.projects);
    const tools = list(resumeData?.tools);

    return (
        <div className="container" style={{ paddingTop: '10%' }}>
            <div className="row h-100">
                <div className="col col-12 col-expand">
                    <h1 className="display-4 text-bold text-indigo">Resume</h1>
                    <p>You can get in touch with me <Link to="/contact">here</Link> or download the PDF version:</p>
                    <div className="resume-blocks">
                        <a href="/assets/files/resume.pdf" className="btn btn-indigo">Download Resume PDF</a>
                    </div>
                    {resumeData?.basics?.summary && <p className="resume-summary"><InlineText>{resumeData.basics.summary}</InlineText></p>}
                    {!resumeData && !resumeError && <p>Loading resume details...</p>}
                    {resumeError && <p>Resume details could not be loaded. The PDF download is still available.</p>}
                </div>

                {work.length > 0 && <div id="workExperience" className="col col-6 col-expand">
                    <h2 className="display-6">{resumeData?.headings?.work || 'Work Experience'}</h2>
                    {
                        work.map((job, index) => {
                            const duration = calculateTimeDifference(job.startDate, job.endDate);
                            const title = `${job.company} - ${job.position}`;

                            return (
                                <div className="resume-blocks" key={`${job.company}-${index}`}>
                                    <p className="lead">
                                        {job.website
                                            ? <a href={job.website} target="_blank" rel="noreferrer">{title}</a>
                                            : title}
                                    </p>
                                    <p>
                                        {job.location && `${job.location} | `}
                                        {job.startDate} - {job.endDate || 'Present'}
                                        {formatDuration(duration)}
                                    </p>
                                    {list(job.highlights).length > 0 && <ul>
                                        {
                                            job.highlights.map((highlight, highlightIndex) => (
                                                <li key={highlightIndex}><InlineText>{highlight}</InlineText></li>
                                            ))
                                        }
                                    </ul>}
                                </div>
                            );
                        })
                    }
                </div>}

                {skills.length > 0 && <div id="skills" className="col col-6 col-expand">
                    <h2 className="display-6">{resumeData?.headings?.skills || 'Skills'}</h2>
                    <div className="resume-blocks">
                        <ul>
                            {skills.map((skill, index) => <li key={index}>{formatSkill(skill)}</li>)}
                        </ul>
                    </div>
                </div>}

                {tools.length > 0 && <div id="tools" className="col col-6 col-expand">
                    <h2 className="display-6">Tools / Frameworks</h2>
                    <div className="resume-blocks">
                        <ul>
                            {tools.map((tool, index) => <li key={index}><InlineText>{tool}</InlineText></li>)}
                        </ul>
                    </div>
                </div>}

                {education.length > 0 && <div id="education" className="col col-6 col-expand">
                    <h2 className="display-6">{resumeData?.headings?.education || 'Education'}</h2>
                    {
                        education.map((school, index) => (
                            <div className="resume-blocks" key={`${school.institution}-${index}`}>
                                <p className="lead">{school.institution}</p>
                                <p>{[school.area, school.studyType].filter(Boolean).join(' - ')}</p>
                                <p>{school.startDate} - {school.endDate || 'Present'}</p>
                            </div>
                        ))
                    }
                </div>}

                {projects.length > 0 && <div id="projects" className="col col-6 col-expand">
                    <h2 className="display-6">{resumeData?.headings?.projects || 'Projects'}</h2>
                    {
                        projects.map((project, index) => (
                            <div className="resume-blocks" key={`${project.name}-${index}`}>
                                <p className="lead">
                                    {project.url || project.website
                                        ? <a href={project.url || project.website} target="_blank" rel="noreferrer">{project.name}</a>
                                        : project.name}
                                </p>
                                <p><InlineText>{project.description || project.summary}</InlineText></p>
                                {list(project.keywords || project.skills).length > 0 && <ul>
                                    {list(project.keywords || project.skills).map((keyword, keywordIndex) => (
                                        <li key={keywordIndex}><InlineText>{keyword}</InlineText></li>
                                    ))}
                                </ul>}
                            </div>
                        ))
                    }
                    <div className="resume-blocks">
                        <Link className="btn btn-indigo" to="/portfolio">See More Projects</Link>
                    </div>
                </div>}

                {awards.length > 0 && <div id="awards" className="col col-6 col-expand">
                    <h2 className="display-6">{resumeData?.headings?.awards || 'Awards'}</h2>
                    {
                        awards.map((award, index) => (
                            <div className="resume-blocks" key={`${award.title}-${index}`}>
                                <p className="lead">{award.awarder}</p>
                                <p>{award.title}</p>
                                <p>{award.date}</p>
                                <p>{award.summary}</p>
                            </div>
                        ))
                    }
                </div>}
            </div>
        </div>
    );
}

export default Resume;
